/**
 * AppErrorBoundary
 *
 * Catches rendering errors and displays a Direction-A styled fallback UI.
 * The Better Stack RUM tag auto-captures thrown exceptions with session
 * replay context; componentDidCatch additionally emits a funnel-tagged event
 * (error boundaries swallow errors from window handlers, so without this the
 * crash would be invisible to RUM).
 *
 * The fallback is a functional sub-component so it can use the useT() hook —
 * the previous class-only render bypassed i18n entirely (hardcoded English on
 * the crash screen) and was the only component left using inline style={{}}
 * instead of the design-system Tailwind tokens.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertCircle } from 'lucide-react'
import { useT } from '@/contexts/I18nContext'
import { track } from '@/lib/betterstack'

interface ErrorFallbackProps {
  message: string | undefined
  onRetry: () => void
  onReport: () => void
}

function ErrorFallback({ message, onRetry, onReport }: ErrorFallbackProps) {
  const t = useT()
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-canvas p-8">
      <div className="w-full max-w-sm rounded-2xl border border-hairline bg-surface-soft p-8 text-center shadow-card">
        <AlertCircle className="mx-auto h-10 w-10 text-error" strokeWidth={1.5} />
        <h2 className="mt-4 font-headline text-xl font-semibold text-on-surface">
          {t('error.title')}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-on-surface-variant">
          {message || t('error.description')}
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <button
            onClick={onRetry}
            className="min-w-[120px] rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-on-primary transition-colors hover:bg-primary-active"
          >
            {t('error.tryAgain')}
          </button>
          <button
            onClick={onReport}
            className="min-w-[120px] rounded-xl border-2 border-hairline px-6 py-3 text-sm font-semibold text-on-surface-variant transition-colors hover:bg-surface-container-low"
          >
            {t('error.report')}
          </button>
        </div>
      </div>
    </div>
  )
}

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class AppErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    track('react-render-error', {
      message: error.message,
      componentStack: errorInfo.componentStack?.slice(0, 2000),
    })
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null })
  }

  handleReport = (): void => {
    const subject = encodeURIComponent(`App error: ${this.state.error?.message ?? 'unknown'}`)
    window.location.href = `mailto:support@piccard.app?subject=${subject}`
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          message={this.state.error?.message}
          onRetry={this.handleRetry}
          onReport={this.handleReport}
        />
      )
    }

    return this.props.children
  }
}
